import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';

import { Session } from 'state';
import { toCardModel } from './helpers';

type Props = {
  session: Session;
  focused: boolean;
  /** Ref registrar so the sidecar can scroll this card into view. */
  registerRef: (id: number, el: HTMLDivElement | null) => void;
};

/**
 * A rich, progress-only session card. Every value comes from the login payload
 * (GET /sessions) — no in-session fetch, no leaked simulated dates. The accent
 * stripe + status pill encode lifecycle state at a glance.
 */
const SessionCard: React.FC<Props> = ({ session, focused, registerRef }) => {
  const m = toCardModel(session);

  return (
    <div
      ref={el => registerRef(m.id, el)}
      className={`dv-card dv-card--${m.status.tone}${focused ? ' is-focus' : ''}`}
    >
      <div className="dv-card__top">
        <span className={`dv-status dv-status--${m.status.tone}`}>
          {m.status.live && <span className="dv-dot" />}
          {m.status.label}
        </span>
        {m.typeLabel && <span className="dv-type">{m.typeLabel}</span>}
      </div>

      <h4 className="dv-card__title" title={m.title}>
        {m.title}
      </h4>
      <div className="dv-card__sub">{m.subtitle}</div>

      <div className="dv-tags">
        {m.tags.map(t => (
          <span className="dv-tag" key={t}>
            {t}
          </span>
        ))}
      </div>

      <div className="dv-card__mid">
        <div className="dv-aum">{m.aum}</div>
        {m.showProgress && (
          <>
            <div className="dv-prog">
              <span>{m.progressPrimary}</span>
              {m.progressSecondary && <span>{m.progressSecondary}</span>}
            </div>
            <div className="dv-meter">
              <i style={{ width: `${m.progressPct}%` }} />
            </div>
          </>
        )}
      </div>

      <div className="dv-card__foot">
        <span>{m.footText}</span>
        <Link className="dv-cta" target="_blank" to={m.to}>
          {m.cta} <ArrowRightOutlined />
        </Link>
      </div>
    </div>
  );
};

export default SessionCard;
